import { apiClient, type PaginatedResponse } from '../../../api/client'
import { v1Api } from '../../../api/generatedClient'
import buildApiUrl, {
  buildFetchUrl,
  buildPaginationParams,
  type PaginationOptions,
  fetchAllPages,
} from '../../../api/pagination'
import { APIRequestFromJSON } from '../../../generated-types'
import type { V1RequestRequestsPartsSingleRequestRetrieveRequest } from '../../../generated-types/apis/V1Api'
import { RequestrequestApi } from '../../../generated-types/apis/RequestrequestApi'
import { RequestofferApi } from '../../../generated-types/apis/RequestofferApi'
import {
  Configuration,
  ResponseError,
  type InitOverrideFunction,
} from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import {
  createContact,
  createFileInfo,
  createUser,
  getPKfromSelf,
  normalizeEditors,
  parseNestedObject,
  parseNestedObjectList,
} from '../../../utils/functions'
import { createArticle } from '../../Item/utils/functions'
import { type Offer, type Request, type RequestArticleData, type RequestFile } from '../utils/types'
import {
  type PaginatedQueryParameters,
  type GenericFilterType,
} from '../../../api/hooks/utils/types'
import { type EntitySearchResponse } from '../../../hooks/useEntitySearch'

const log = new LogTool({ context: 'RequestAPI', enable: true, logLevel: 'debug' })

/**
 * Filter types for Request queries
 */
export interface RequestFilters extends GenericFilterType {
  request_number?: string
  certificate?: string
  article?: string
  status?: string
  created_by?: string
  created_date?: string
  updated_date?: string
  priority?: string
  country?: string
  remarks?: string
  description?: string
  quantities?: string
  wish_date?: string
  editors?: string
  organization?: string
}

/**
 * Filter types for Offer queries
 */
export interface OfferFilters extends GenericFilterType {
  offer_number?: string
  request_number?: string
  article?: string
  status?: string
  remarks?: string
  prices?: string
  editors?: string
  created_at?: string
  last_modified_at?: string
}

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }

  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    },
  })
}

/**
 * Create API instances
 */
function getRequestsApi(): RequestrequestApi {
  return new RequestrequestApi(getApiConfiguration())
}

function getOffersApi(): RequestofferApi {
  return new RequestofferApi(getApiConfiguration())
}

function buildV1InitOverrides(accessSecret?: string): InitOverrideFunction | undefined {
  if (!accessSecret) return undefined
  return async ({ init }) =>
    Promise.resolve({
      ...init,
      headers: {
        ...(init.headers ?? {}),
        'X-ACCESS-SECRET': accessSecret,
      },
    })
}

/**
 * Create RequestArticleData from API response
 */
function createRequestArticleData(data: any): RequestArticleData {
  return {
    article: createArticle(data.article),
    quantity: data.quantity ?? 1,
  }
}

/**
 * Create Request from API response
 */
export function createRequest(requestResponse: any): Request {
  const apiRequest = APIRequestFromJSON(requestResponse)

  // Parse request_articles from backend (snake_case) to requestArticles (camelCase)
  const requestArticles: RequestArticleData[] | undefined = requestResponse.request_articles
    ? requestResponse.request_articles.map(createRequestArticleData)
    : undefined

  return {
    ...apiRequest,
    key: getPKfromSelf(requestResponse.self),
    article: parseNestedObject(requestResponse.article, createArticle),
    requestArticles,
    certificate: requestResponse.certificate,
    quantities: requestResponse.quantities,
    calculatedCosts: requestResponse.calculated_costs,
    calculatedCostBreakdown: requestResponse.calculated_cost_breakdown,
    reportedCosts: requestResponse.reported_costs,
    editors: parseNestedObjectList(requestResponse.editors, createUser),
    contact: parseNestedObject(apiRequest.contact, createContact),
    files: parseNestedObjectList(requestResponse.files, createFileInfo),
    read: requestResponse.read,
    is_temp: requestResponse.is_temp,
    organizationName: requestResponse.root_account_name ?? null,
    accessSecret: requestResponse.access_secret ?? requestResponse.accessSecret,
  }
}

/**
 * Create Offer from API response
 */
export function createOffer(offerResponse: any): Offer {
  return {
    key: getPKfromSelf(offerResponse.self),
    self: offerResponse.self,
    request: offerResponse.request,
    remarks: offerResponse.remarks,
    prices: offerResponse.prices,
    status: offerResponse.status,
    editors: parseNestedObjectList(offerResponse.editors, createUser),
    createdAt: offerResponse.created_at,
    lastModifiedAt: offerResponse.last_modified_at,
    article: offerResponse.article,
    files: parseNestedObjectList(offerResponse.files, createFileInfo),
    read: offerResponse.read,
  }
}

/**
 * Create RequestFile from API response
 */
export function createRequestFile(fileResponse: any): RequestFile {
  const file = {
    ...fileResponse,
    key: fileResponse.self,
    createdAt: new Date(fileResponse.created_at).toLocaleString(),
  }
  delete file.created_at
  return file
}

export function inputToAPIRequestJSON(r: any): any {
  log.info('inputToAPIRequestJSON received:', r)
  log.debug('articlesWithQuantities:', r.articlesWithQuantities)
  log.debug('articles:', r.articles)
  log.debug('article:', r.article)

  // Format wishDate to YYYY-MM-DD if it's a Date object
  let wishDate = r.wishDate
  if (wishDate instanceof Date) {
    wishDate = wishDate.toISOString().split('T')[0]
  }

  const editors = normalizeEditors(r.editors)

  // Convert editor UUIDs to full URLs if needed
  // normalizeEditors already extracts the 'self' values from user objects
  const editorUrls = editors?.map(editor => {
    // If it's already any kind of URL path (absolute or relative), return as-is
    if (editor.includes('/')) {
      return editor
    }
    // Only if it's a plain UUID (no slashes), convert to full URL
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/'
    const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : envApiUrl + '/'
    return `${baseUrl}v1/accounts/users/${editor}/`
  })

  // Handle articles with quantities - preferred format for new requests
  // Note: Use snake_case for direct API calls (not going through generated types)
  let articlesField: any = undefined

  const hasArticlesWithQuantities =
    r.articlesWithQuantities &&
    Array.isArray(r.articlesWithQuantities) &&
    r.articlesWithQuantities.length > 0
  const hasArticles = r.articles && Array.isArray(r.articles) && r.articles.length > 0
  const hasArticle = !!r.article

  log.debug('Article source check:', { hasArticlesWithQuantities, hasArticles, hasArticle })

  if (hasArticlesWithQuantities) {
    // New format: articles with individual quantities
    articlesField = {
      articles_with_quantities: r.articlesWithQuantities
        .map((item: any) => ({
          article: typeof item.article === 'string' ? item.article : item.article?.self,
          quantity: item.quantity || 1,
        }))
        .filter((item: any) => item.article), // Filter out any undefined/null article values
    }
    log.info('Using articles_with_quantities:', articlesField.articles_with_quantities)
  } else if (hasArticles) {
    // Legacy format: articles array without quantities (default quantity 1)
    articlesField = {
      articles: r.articles
        .map((article: any) => (typeof article === 'string' ? article : article?.self))
        .filter((url: string) => url), // Filter out any undefined/null values
    }
    log.info('Using articles array:', articlesField.articles)
  } else if (hasArticle) {
    // Backward compatibility: convert single article to articles array
    const articleUrl = typeof r.article === 'string' ? r.article : r.article?.self
    if (articleUrl) {
      articlesField = { articles: [articleUrl] }
      log.info('Using single article:', articlesField.articles)
    }
  } else {
    log.warn('No articles found in request data!')
  }

  const result = {
    ...articlesField,
    ...(r.remarks && { remarks: r.remarks }),
    ...(r.certificate && {
      certificate: typeof r.certificate === 'string' ? r.certificate : r.certificate?.value,
    }),
    ...(r.description && { description: r.description }),
    ...(r.price && { price: parseInt(r.price) }),
    ...(r.currency && { currency: r.currency }),
    ...(r.quantities && { quantities: [...r.quantities] }),
    ...(wishDate && { wish_date: wishDate }),
    ...(r.status !== false && r.status !== undefined && { status: parseInt(r.status) }),
    ...(r.priority !== undefined && { priority: r.priority }),
    ...(r.country && { country: typeof r.country === 'string' ? r.country : r.country?.value }),
    ...(r.shippingAddress && { shipping_address: r.shippingAddress?.value }),
    ...(r.billingAddress && { billing_address: r.billingAddress?.value }),
    ...(editorUrls && editorUrls.length >= 0 && { editors: editorUrls }),
  }

  log.info('Final API JSON to send:', JSON.stringify(result, null, 2))
  return result
}

/**
 * Convert Offer form input to API JSON
 */
export function inputToAPIOfferJSON(o: any): any {
  const editors = normalizeEditors(o.editors)

  // Convert editor UUIDs to full URLs if needed
  // normalizeEditors already extracts the 'self' values from user objects
  const editorUrls = editors?.map(editor => {
    // If it's already any kind of URL path (absolute or relative), return as-is
    if (editor.includes('/')) {
      return editor
    }
    // Only if it's a plain UUID (no slashes), convert to full URL
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/'
    const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : envApiUrl + '/'
    return `${baseUrl}v1/accounts/users/${editor}/`
  })

  // Extract request URL - handle both object with 'self' property and direct URL string
  let requestUrl = undefined
  if (o.request) {
    if (typeof o.request === 'string') {
      requestUrl = o.request
    } else if (o.request.self) {
      requestUrl = o.request.self
    }
  }

  return {
    ...(requestUrl && { request: requestUrl }),
    ...(o.remarks && { remarks: o.remarks }),
    ...(o.prices && { prices: o.prices }),
    ...(o.status !== undefined && { status: parseInt(o.status) }),
    ...(editorUrls && editorUrls.length >= 0 && { editors: editorUrls }),
  }
}

/**
 * Fetch paginated requests from API
 */
export async function fetchRequests(
  options: PaginatedQueryParameters<Request, RequestFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<Request>> {
  log.info('Fetching requests with options:', options)

  // Use the new buildApiUrl for smart query support
  const url = buildApiUrl(
    'v1/request/requests/',
    options as PaginatedQueryParameters<Request, RequestFilters>,
    ids
  )

  const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(url)

  const requests = response.results ? response.results.map(createRequest) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: requests,
  }
}

/**
 * Search requests using Elasticsearch
 */
export async function searchRequests(
  options: PaginatedQueryParameters<Request, RequestFilters> = {}
): Promise<EntitySearchResponse<Request>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''

  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }

  const url = buildApiUrl('v1/search/requests/requests/', { filters: searchFilters })
  return apiClient.get<EntitySearchResponse<Request>>(url)
}

/**
 * Fetch a single request by ID or URL
 */
export async function fetchRequest(idOrUrl: string | number, expand?: string[]): Promise<Request> {
  log.info('Fetching request:', idOrUrl)

  const url =
    typeof idOrUrl === 'number' || !idOrUrl.includes('/')
      ? `v1/request/requests/${idOrUrl}/`
      : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createRequest(response)
}

/**
 * Fetch the per-part request that contains only the selected part
 */
export async function fetchSinglePartRequest({
  requestPk,
  partId,
  accessSecret,
}: {
  requestPk: string | number
  partId: string | number
  accessSecret?: string
}): Promise<Request | null> {
  log.info('Fetching single-part request:', { requestPk, partId })

  try {
    const requestParams: V1RequestRequestsPartsSingleRequestRetrieveRequest = {
      requestPk: String(requestPk),
      id: String(partId),
    }
    const initOverrides = buildV1InitOverrides(accessSecret)
    const response = await v1Api.v1RequestRequestsPartsSingleRequestRetrieveRaw(
      requestParams,
      initOverrides
    )
    const payload = await response.raw.json()
    return createRequest(payload)
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Fetch paginated offers from API
 */
export async function fetchOffers(
  options: PaginatedQueryParameters<Offer, OfferFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<Offer>> {
  log.info('Fetching offers with options:', options)

  // Use the new buildApiUrl for smart query support
  const url = buildApiUrl(
    'v1/request/offers/',
    options as PaginatedQueryParameters<Offer, OfferFilters>,
    ids
  )

  const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(url)

  const offers = response.results ? response.results.map(createOffer) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: offers,
  }
}

/**
 * Search offers using Elasticsearch
 */
export async function searchOffers(
  options: PaginatedQueryParameters<Offer, OfferFilters> = {}
): Promise<EntitySearchResponse<Offer>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''

  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }

  const url = buildApiUrl('v1/search/requests/offers/', { filters: searchFilters })
  return apiClient.get<EntitySearchResponse<Offer>>(url)
}

/**
 * Fetch a single offer by ID or URL
 */
export async function fetchOffer(idOrUrl: string | number, expand?: string[]): Promise<Offer> {
  log.info('Fetching offer:', idOrUrl)

  const url =
    typeof idOrUrl === 'number' || !idOrUrl.includes('/')
      ? `v1/request/offers/${idOrUrl}/`
      : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createOffer(response)
}

/**
 * Create a new request
 * Note: We use apiClient directly instead of generated API because
 * the generated API's ToJSON function strips fields that aren't in the generated types.
 */
export async function createRequestApi(requestData: any): Promise<Request> {
  log.info('Creating request:', requestData)

  const json = inputToAPIRequestJSON(requestData)
  log.debug('Request JSON to send:', json)

  const response = await apiClient.post<any>('v1/request/requests/', json)
  return createRequest(response)
}

/**
 * Create a new offer
 */
export async function createOfferApi(offerData: any): Promise<Offer> {
  log.info('Creating offer:', offerData)

  const json = inputToAPIOfferJSON(offerData)
  const response = await getOffersApi().v1RequestOffersCreate({
    aPIOfferRequest: json,
  })

  return createOffer(response)
}

/**
 * Update an existing request
 */
export async function updateRequestApi(
  idOrSelf: string | number,
  requestData: any
): Promise<Request> {
  log.info('Updating request:', idOrSelf, requestData)

  const json = inputToAPIRequestJSON(requestData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getRequestsApi().v1RequestRequestsUpdate({
      id: idOrSelf,
      aPIRequestRequest: json,
    })
    return createRequest(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/request/requests/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)
  return createRequest(response)
}

/**
 * Update an existing offer
 */
export async function updateOfferApi(idOrSelf: string | number, offerData: any): Promise<Offer> {
  log.info('Updating offer:', idOrSelf, offerData)

  const json = inputToAPIOfferJSON(offerData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getOffersApi().v1RequestOffersUpdate({
      id: idOrSelf,
      aPIOfferRequest: json,
    })
    return createOffer(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/request/offers/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)
  return createOffer(response)
}

/**
 * Patch an existing request (partial update)
 */
export async function patchRequestApi(
  idOrSelf: string | number,
  requestData: Partial<any>
): Promise<Request> {
  log.info('Patching request:', idOrSelf, requestData)

  const json = inputToAPIRequestJSON(requestData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getRequestsApi().v1RequestRequestsPartialUpdate({
      id: idOrSelf,
      aPIPatchedRequestRequest: json,
    })
    return createRequest(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/request/requests/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createRequest(response)
}

/**
 * Patch an existing offer (partial update)
 */
export async function patchOfferApi(
  idOrSelf: string | number,
  offerData: Partial<any>
): Promise<Offer> {
  log.info('Patching offer:', idOrSelf, offerData)

  const json = inputToAPIOfferJSON(offerData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getOffersApi().v1RequestOffersPartialUpdate({
      id: idOrSelf,
      aPIPatchedOfferRequest: json,
    })
    return createOffer(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/request/offers/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createOffer(response)
}

/**
 * Delete a request
 */
export async function deleteRequestApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting request:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await getRequestsApi().v1RequestRequestsDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/request/requests/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete an offer
 */
export async function deleteOfferApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting offer:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await getOffersApi().v1RequestOffersDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/request/offers/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete multiple requests
 */
export async function deleteRequestsApi(requests: Request[]): Promise<void> {
  log.info('Deleting requests:', requests.length)

  const deletePromises = requests.map(request => deleteRequestApi(request.self))
  await Promise.all(deletePromises)
}

/**
 * Delete multiple offers
 */
export async function deleteOffersApi(offers: Offer[]): Promise<void> {
  log.info('Deleting offers:', offers.length)

  const deletePromises = offers.map(offer => deleteOfferApi(offer.self))
  await Promise.all(deletePromises)
}

/**
 * Fetch request files
 */
export async function fetchRequestFiles(requestIdOrSelf: string | number): Promise<RequestFile[]> {
  log.info('Fetching request files:', requestIdOrSelf)

  const url =
    typeof requestIdOrSelf === 'string' && requestIdOrSelf.includes('/')
      ? `${requestIdOrSelf}files/`
      : `v1/request/requests/${requestIdOrSelf}/files/`

  const response = await apiClient.get<PaginatedResponse<any>>(url)
  return response.results.map((f: any) => createRequestFile(f))
}

/**
 * Upload file to request
 */
export async function uploadRequestFile(requestSelf: string, file: File): Promise<void> {
  log.info('Uploading file to request:', requestSelf)

  const formData = new FormData()
  formData.append('request', requestSelf)
  formData.append('document', file)

  await apiClient.post(
    `${requestSelf}files/`,
    formData,
    {
      'Content-Type': 'multipart/form-data',
    },
    true
  )
}

/**
 * Upload file to offer
 */
export async function uploadOfferFile(offerSelf: string, file: File): Promise<void> {
  log.info('Uploading file to offer:', offerSelf)

  const formData = new FormData()
  formData.append('offer', offerSelf)
  formData.append('document', file)

  await apiClient.post(
    'v1/request/offer_files/',
    formData,
    {
      'Content-Type': 'multipart/form-data',
    },
    true
  )
}

/**
 * Complete a temporary guest request
 */
export async function completeRequestApi(
  idOrSelf: string | number,
  requestData: any,
  accessSecret?: string,
  expand?: string[]
): Promise<Request> {
  log.info('Completing request:', idOrSelf)

  let url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/request/requests/${idOrSelf}/complete/`
      : `${idOrSelf}complete/`

  // Add expand parameter if provided
  if (expand && expand.length > 0) {
    const params = [`expand=${expand.join(',')}`]
    url = url.includes('?') ? `${url}&${params.join('&')}` : `${url}?${params.join('&')}`
  }

  const headers: Record<string, string> = {}
  if (accessSecret) {
    headers['X-ACCESS-SECRET'] = accessSecret
  }

  const response = await apiClient.patch<any>(url, requestData, headers)
  return createRequest(response)
}
